import { useState, useRef, useEffect, useCallback, type KeyboardEvent, type ClipboardEvent } from 'react'
import { Send, Zap, Brain, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useZinsserStore } from '@/stores/useZinsserStore'
import { uploadZinsserAttachment } from '@/lib/api'

interface ZinsserChatInputProps {
  onSubmit: (content: string, forceSummary?: boolean) => void
  disabled?: boolean
  placeholder?: string
}

const IMAGE_SIZE_LIMIT = 5 * 1024 * 1024 // 5MB

export function ZinsserChatInput({ onSubmit, disabled, placeholder }: ZinsserChatInputProps) {
  const [value, setValue] = useState('')
  const [isUploadingImage, setIsUploadingImage] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [uploadSuccess, setUploadSuccess] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const scrollPositionRef = useRef<number>(0)

  const sessionId = useZinsserStore((state) => state.sessionId)
  const runMode = useZinsserStore((state) => state.runMode)
  const setRunMode = useZinsserStore((state) => state.setRunMode)
  const incrementAttachmentVersion = useZinsserStore((state) => state.incrementAttachmentVersion)

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current
    if (textarea) {
      textarea.style.height = 'auto'
      textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`
    }
  }, [value])

  // Clear upload error after 3 seconds
  useEffect(() => {
    if (uploadError) {
      const timer = setTimeout(() => setUploadError(null), 3000)
      return () => clearTimeout(timer)
    }
  }, [uploadError])

  // Clear upload success after 2 seconds
  useEffect(() => {
    if (uploadSuccess) {
      const timer = setTimeout(() => setUploadSuccess(false), 2000)
      return () => clearTimeout(timer)
    }
  }, [uploadSuccess])

  // Save scroll position before focus (iOS keyboard fix)
  const handleFocus = useCallback(() => {
    scrollPositionRef.current = window.scrollY
    // Use requestAnimationFrame to restore scroll after iOS keyboard causes unwanted scroll
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (Math.abs(window.scrollY - scrollPositionRef.current) > 50) {
          window.scrollTo(0, scrollPositionRef.current)
        }
      })
    })
  }, [])

  const handleSubmit = () => {
    if (value.trim() && !disabled) {
      onSubmit(value.trim())
      setValue('')
      // Blur on mobile after submit to hide keyboard
      if (window.innerWidth < 768) {
        textareaRef.current?.blur()
      }
    }
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // Submit on Enter (without shift)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit()
    }
  }

  const handlePaste = useCallback(async (e: ClipboardEvent<HTMLTextAreaElement>) => {
    if (!sessionId) return

    const items = e.clipboardData?.items
    if (!items) return

    // Look for image items in clipboard
    for (const item of Array.from(items)) {
      if (item.type.startsWith('image/')) {
        e.preventDefault() // Prevent default paste for images

        const blob = item.getAsFile()
        if (!blob) continue

        // Check size limit
        if (blob.size > IMAGE_SIZE_LIMIT) {
          setUploadError('Image exceeds 5MB limit')
          return
        }

        // Create a File object with a proper name
        const extension = item.type.split('/')[1] || 'png'
        const filename = `screenshot-${Date.now()}.${extension}`
        const file = new File([blob], filename, { type: item.type })

        // Upload the image
        setIsUploadingImage(true)
        setUploadError(null)

        try {
          await uploadZinsserAttachment(sessionId, file)
          // Success - image is now in attachments panel
          // Trigger attachments panel refresh
          incrementAttachmentVersion()
          // Show success confirmation
          setUploadSuccess(true)
        } catch (err) {
          console.error('Failed to upload pasted image:', err)
          setUploadError(err instanceof Error ? err.message : 'Upload failed')
        } finally {
          setIsUploadingImage(false)
        }

        return // Only handle first image
      }
    }
    // If no images, let the default paste behavior handle text
  }, [sessionId, incrementAttachmentVersion])

  const toggleRunMode = () => {
    setRunMode(runMode === 'fast' ? 'deep' : 'fast')
  }

  return (
    <div className="relative">
      {/* Upload indicator */}
      {isUploadingImage && (
        <div className="absolute -top-8 left-0 right-0 flex items-center justify-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="w-3 h-3 animate-spin" />
          <span>Uploading screenshot...</span>
        </div>
      )}

      {/* Upload error */}
      {uploadError && (
        <div className="absolute -top-8 left-0 right-0 flex items-center justify-center gap-2 text-xs text-red-500">
          <span>{uploadError}</span>
        </div>
      )}

      {/* Upload success */}
      {uploadSuccess && (
        <div className="absolute -top-8 left-0 right-0 flex items-center justify-center gap-2 text-xs text-green-600">
          <span>Screenshot added to Attachments</span>
        </div>
      )}

      <textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onFocus={handleFocus}
        disabled={disabled || isUploadingImage}
        placeholder={placeholder}
        rows={1}
        className={cn(
          'w-full resize-none rounded-lg border border-border bg-background pl-12 pr-12 py-3',
          'text-base md:text-sm text-foreground placeholder:text-muted-foreground',
          'focus:outline-none focus:ring-1 focus:ring-foreground/20',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          'transition-colors'
        )}
      />
      {/* Run mode toggle - left side */}
      <button
        onClick={toggleRunMode}
        disabled={disabled}
        className={cn(
          'absolute left-2 top-1/2 -translate-y-1/2',
          'p-2 -m-1 rounded-md',
          'transition-colors',
          'disabled:opacity-30 disabled:cursor-not-allowed',
          runMode === 'fast'
            ? 'text-amber-500 hover:text-amber-600'
            : 'text-violet-500 hover:text-violet-600'
        )}
        aria-label={runMode === 'fast' ? 'Fast mode (no extended thinking)' : 'Deep mode (extended thinking enabled)'}
        title={runMode === 'fast' ? 'Fast mode - click for Deep' : 'Deep mode - click for Fast'}
      >
        {runMode === 'fast' ? (
          <Zap className="w-5 h-5 md:w-4 md:h-4" strokeWidth={1.5} />
        ) : (
          <Brain className="w-5 h-5 md:w-4 md:h-4" strokeWidth={1.5} />
        )}
      </button>
      {/* Send button - right side */}
      <button
        onClick={handleSubmit}
        disabled={disabled || !value.trim() || isUploadingImage}
        className={cn(
          'absolute right-2 top-1/2 -translate-y-1/2',
          'p-3 -m-1 rounded-md',
          'text-muted-foreground hover:text-foreground active:text-foreground',
          'disabled:opacity-30 disabled:cursor-not-allowed',
          'transition-colors'
        )}
        aria-label="Send message"
      >
        <Send className="w-5 h-5 md:w-4 md:h-4" strokeWidth={1.5} />
      </button>
    </div>
  )
}
